import React, { useEffect, useState } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { fetchJson } from "../../services/api";
import { TrendsResponse, TrendDay, TrendHour } from "../types";
import { PageHeader, SectionCard, Loader, ErrorMsg, refreshBtnStyle } from "../components/Common";
import "./TrendsPage.css";

const TrendsPage: React.FC = () => {
  const [data, setData]       = useState<TrendDay[]>([]);
  const [hourly, setHourly]   = useState<TrendHour[]>([]);
  const [days, setDays]       = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = (d = days) => {
    setLoading(true);
    fetchJson<TrendsResponse>("/monitor/trends/", { days: d })
      .then((res) => {
        if (!res?.trends) throw new Error("No trends data received.");
        setData(res.trends);
        setHourly(res.hourly || []);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const formatted = data.map((row) => ({
    ...row,
    date: new Date(row.stat_date).toLocaleDateString("en-IN", { month: "short", day: "numeric" }),
  }));

  const totalCalls   = data.reduce((s, r) => s + r.total_calls,   0);
  const totalSuccess = data.reduce((s, r) => s + r.success_count, 0);
  const totalFailed  = data.reduce((s, r) => s + r.failed_count,  0);
  const successRate  = totalCalls ? Math.round((totalSuccess / totalCalls) * 100) : 0;
  const failedRate   = totalCalls ? Math.round((totalFailed  / totalCalls) * 100) : 0;

  if (loading) return <Loader />;
  if (error)   return <ErrorMsg msg={error} onRetry={() => load()} />;

  return (
    <div>
      <PageHeader title="Trends" subtitle="Historical API volume and performance" />

      {/* Day selector */}
      <div className="trends-page__filter-bar">
        {[7, 14, 30].map((d) => (
          <button
            key={d}
            onClick={() => { setDays(d); load(d); }}
            className={`trends-page__filter-btn${days === d ? " trends-page__filter-btn--active" : ""}`}
            style={{
              ...refreshBtnStyle,
              background: days === d ? "var(--info)" : "var(--surface)",
              color: days === d ? "#fff" : "var(--text)",
            }}
          >
            Last {d} days
          </button>
        ))}
        <button onClick={() => load(days)} style={{ ...refreshBtnStyle, marginLeft: "auto" }}>↻ Refresh</button>
      </div>

      {/* Summary KPIs */}
      <div className="trends-page__stats-grid">
        {[
          { label: "Total Calls",   value: totalCalls.toLocaleString(),   color: "#3b82f6" },
          { label: "Successful",    value: totalSuccess.toLocaleString(),  color: "#22c55e" },
          { label: "Failed",        value: totalFailed.toLocaleString(),   color: "#ef4444" },
          { label: "Success Rate",  value: `${successRate}%`,              color: "#22c55e" },
          { label: "Failure Rate",  value: `${failedRate}%`,               color: "#ef4444" },
        ].map(({ label, value, color }) => (
          <div key={label} className="trends-page__stat-card" style={{ borderLeftColor: color }}>
            <div className="trends-page__stat-label">{label}</div>
            <div className="trends-page__stat-value" style={{ color }}>{value}</div>
          </div>
        ))}
      </div>

      {data.length === 0 ? (
        <SectionCard title="No Data">
          <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>
            No trend data yet — the nightly aggregation job populates this from MongoDB into Azure SQL.
          </div>
        </SectionCard>
      ) : (
        <>
          <SectionCard title={`API Call Volume — Last ${days} Days`}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={formatted} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "var(--muted)", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "var(--muted)", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={ttStyle} />
                <Legend wrapperStyle={{ fontSize: 12, color: "var(--muted-alt)" }} />
                <Bar dataKey="success_count" name="Success" stackId="a" fill="#22c55e" />
                <Bar dataKey="failed_count"  name="Failed"  stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </SectionCard>

          <SectionCard title={`Response Time Trend — Last ${days} Days`}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={formatted} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "var(--muted)", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "var(--muted)", fontSize: 11 }} axisLine={false} tickLine={false} unit="ms" />
                <Tooltip contentStyle={ttStyle} formatter={(v) => [`${v}ms`]} />
                <Legend wrapperStyle={{ fontSize: 12, color: "var(--muted-alt)" }} />
                <Line type="monotone" dataKey="avg_response_ms" name="Avg RT" stroke="#f59e0b" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="max_response_ms" name="Max RT" stroke="#ef4444" strokeWidth={1} dot={false} strokeDasharray="4 4" />
                <Line type="monotone" dataKey="min_response_ms" name="Min RT" stroke="#22c55e" strokeWidth={1} dot={false} strokeDasharray="4 4" />
              </LineChart>
            </ResponsiveContainer>
          </SectionCard>

          <SectionCard title={`Hourly Breakdown — Last ${days} Days`}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={hourly} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "var(--muted)", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "var(--muted)", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={ttStyle} />
                <Legend wrapperStyle={{ fontSize: 12, color: "var(--muted-alt)" }} />
                <Bar dataKey="success_count" name="Success" stackId="a" fill="#22c55e" />
                <Bar dataKey="failed_count"  name="Failed"  stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </SectionCard>

          <SectionCard title="Daily Breakdown Table">
            <div className="trends-page__table-wrap">
              <table className="trends-page__table">
                <thead>
                  <tr className="trends-page__table-header">
                    {["Date", "Total", "Success", "Failed", "Avg RT", "Max RT", "Min RT"].map((h) => (
                      <th key={h} className="trends-page__table-cell" style={{ textAlign: "left", fontSize: 11, fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...formatted].reverse().map((row) => (
                    <tr key={row.stat_date} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td className="trends-page__table-cell">{row.date}</td>
                      <td className="trends-page__table-cell">{row.total_calls.toLocaleString()}</td>
                      <td className="trends-page__table-cell" style={{ color: "#4ade80" }}>{row.success_count.toLocaleString()}</td>
                      <td className="trends-page__table-cell" style={{ color: "#f87171" }}>{row.failed_count.toLocaleString()}</td>
                      <td className="trends-page__table-cell" style={{ color: "#fbbf24" }}>{row.avg_response_ms}ms</td>
                      <td className="trends-page__table-cell" style={{ color: "#f87171" }}>{row.max_response_ms}ms</td>
                      <td className="trends-page__table-cell" style={{ color: "#4ade80" }}>{row.min_response_ms}ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
};

export default TrendsPage;

const ttStyle: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--text)" };
